import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const CartContext = createContext(null);

const STORAGE = 'sb_cart_v1';

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE);
      return raw ? JSON.parse(raw) : [];
    } catch (e) { return []; }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE, JSON.stringify(items));
  }, [items]);

  const addItem = (item, size = null, qty = 1, notes = '') => {
    const unitPrice = size
      ? (item.sizes.find((s) => s.name === size)?.price ?? 0)
      : (item.price ?? 0);
    const key = `${item.id}__${size || ''}`;
    setItems((prev) => {
      const existing = prev.find((p) => p.key === key);
      if (existing) {
        return prev.map((p) => p.key === key ? { ...p, qty: p.qty + qty } : p);
      }
      return [
        ...prev,
        {
          key,
          item_id: item.id,
          name: item.name,
          image_url: item.image_url,
          size,
          unit_price: unitPrice,
          qty,
          notes,
        },
      ];
    });
  };

  const updateQty = (key, qty) => {
    setItems((prev) => prev.map((p) => p.key === key ? { ...p, qty: Math.max(0, qty) } : p).filter((p) => p.qty > 0));
  };

  const updateNotes = (key, notes) => {
    setItems((prev) => prev.map((p) => p.key === key ? { ...p, notes } : p));
  };

  const removeItem = (key) => setItems((prev) => prev.filter((p) => p.key !== key));
  const clear = () => setItems([]);

  const subtotal = useMemo(() => items.reduce((s, i) => s + i.unit_price * i.qty, 0), [items]);
  const count = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);

  return (
    <CartContext.Provider value={{ items, addItem, updateQty, updateNotes, removeItem, clear, subtotal, count }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside CartProvider');
  return ctx;
};
